import { PointCloudOctree } from '@datalabs-jp/three-loader'
import { MtlTextureMappingV1 } from '@editor/interfaces/meshFiles'
import { DoubleSide, Material, Mesh, Object3D, Points } from 'three'
import { MTLLoader, MaterialCreator, MaterialInfo, OBJLoader } from 'three-stdlib'

/**
 * Recursively go through a Group and collect all Mesh objects.
 * @param object The Object3D to traverse.
 * @return An array of Mesh objects found within the Group.
 */
export const collectMeshesFromGroup = (object: Object3D): Mesh[] => {
  const meshes: Mesh[] = []

  object.traverse((child) => {
    if (child instanceof Mesh) {
      meshes.push(child as Mesh)
      return
    }

    // If the child is not a Mesh, continue traversing its children
    child.children.forEach((grandChild) => {
      meshes.push(...collectMeshesFromGroup(grandChild))
    })
  })

  return meshes
}

/**
 * Load an OBJ file with multiple MTL files, merging their materials.
 * @param objFile URL of the OBJ file
 * @param mtlFiles Array of MTL file URLs and their corresponding texture overrides
 * @param onProgress Optional progress callback. Receives:
 *   - fileName: URL of the file being loaded.
 *   - progress: number from 0 to 1 indicating the percentage complete.
 * @returns
 * @deprecated Use `loadObjV2` instead.
 */
export const loadObj = async (
  objFile: string,
  mtlFiles: MtlTextureMappingV1[],
  onProgress?: (fileName: string, progress: number) => void,
) => {
  if (mtlFiles.length === 0) return null

  const objLoader = new OBJLoader()
  const mtlLoader = new MTLLoader()

  // 1. Load all MTL files in parallel
  const creators = await Promise.all(
    mtlFiles.map(
      ({ mtl_file }) =>
        new Promise<MaterialCreator>((resolve, reject) => {
          mtlLoader.load(
            mtl_file,
            resolve,
            (progressEvent) => {
              if (onProgress && progressEvent.total) {
                const progress = (progressEvent.loaded / progressEvent.total) * 100
                onProgress(mtl_file, progress)
              }
            },
            reject,
          )
        }),
    ),
  )

  if (creators.length === 0) return null

  // 2. Merge all MaterialCreator instances
  const merged = creators[0]
  for (let i = 1; i < creators.length; i += 1) {
    Object.assign(merged.materialsInfo, creators[i].materialsInfo)
    Object.assign(merged.materials, creators[i].materials)
  }

  // 3. Apply your texture override logic to all materials
  mtlFiles.forEach(({ textures }) => {
    Object.entries(textures).forEach(([key, url]) => {
      const attrName = key.toLowerCase()
      Object.values(merged.materialsInfo).forEach((info: MaterialInfo) => {
        if (attrName in info) {
          // @ts-expect-error TS would complain about dynamic key assignment
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          info[attrName] = url
        }
      })
    })
  })

  // 4. Preload + adjust material settings
  merged.preload()
  Object.values(merged.materials).forEach((mat: Material) => {
    mat.side = DoubleSide
    mat.alphaTest = 0.01
  })

  // 5. Load the OBJ with the merged materials
  return new Promise<Object3D>((resolve, reject) => {
    objLoader.setMaterials(merged)
    objLoader.load(
      objFile,
      resolve,
      (progressEvent) => {
        if (onProgress && progressEvent.total) {
          const progress = (progressEvent.loaded / progressEvent.total) * 100
          onProgress(objFile, progress)
        }
      },
      reject,
    )
  })
}

// loadObjV2, loadObjPacked, loadObjMtl, loadMaterialsFromImagePack and tagAsSolidMesh now live in
// @infraloop/common's shared solid-mesh engine. tagAsSolidMesh is re-exported for existing importers.
export { tagAsSolidMesh } from '@infraloop/common'

/**
 * Recursively find objects of a specific type within an Object3D, defined by userData.type.
 *
 * @param object The Object3D to search within.
 * @param type The type to search for, defined in userData.type.
 */
export const findObjectsOfType = <T extends Object3D>(object: Object3D, type: string): T[] => {
  const objects: T[] = []

  // Check if current object matches the type
  if (object.userData.type === type) {
    objects.push(object as T)
  }

  // Recursively check all children
  object.children.forEach((child) => {
    objects.push(...findObjectsOfType<T>(child, type))
  })

  return objects
}

/**
 * Recursively find objects from a list of shapeIds within an Object3D, defined by userData.shapeId.
 *
 * @param object The Object3D to search within.
 * @param shapeIds The list of shapeIds to search for, defined in userData.shapeId.
 * @returns An array of Object3D instances whose userData.shapeId matches any of the provided shapeIds.
 */
export const findObjectsOfShapeIds = (object: Object3D, shapeIds: string[]): Object3D[] => {
  const objects: Object3D[] = []
  const userData = object.userData as { shapeId?: string }

  // Ignore Points / PointCloudOctree objects
  if (object instanceof Points || object instanceof PointCloudOctree) {
    return objects
  }

  // Check if current object matches any of the shapeIds
  if (userData.shapeId && shapeIds.includes(userData.shapeId)) {
    objects.push(object)
  }

  // Recursively check all children
  object.children.forEach((child) => {
    objects.push(...findObjectsOfShapeIds(child, shapeIds))
  })

  return objects
}
